import * as FilePond from 'filepond';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import ApplicationController from './application_controller';

// Register FilePond plugins
FilePond.registerPlugin(
  FilePondPluginImagePreview,
  FilePondPluginFileValidateType,
  FilePondPluginFileValidateSize,
);

/**
 * Profile Photo Upload Controller
 *
 * Handles profile photo uploads using FilePond with S3 direct upload.
 *
 * Data attributes:
 * - data-profile-photo-upload-api-url-value: URL to request presigned upload URL
 * - data-profile-photo-upload-save-url-value: URL to save profile photo after S3 upload
 * - data-profile-photo-upload-redirect-url-value: URL to redirect after success
 * - data-profile-photo-upload-csrf-token-value: CSRF token for API requests
 */
export default class extends ApplicationController {
  static targets = [
    'input',
    'status',
    'statusText',
    'error',
    'errorText',
    'success',
    'saveButton',
    'saveButtonText',
  ];

  static values = {
    apiUrl: String,
    saveUrl: String,
    redirectUrl: String,
    csrfToken: String,
  };

  declare inputTarget: HTMLInputElement;
  declare statusTarget: HTMLElement;
  declare statusTextTarget: HTMLElement;
  declare errorTarget: HTMLElement;
  declare errorTextTarget: HTMLElement;
  declare successTarget: HTMLElement;
  declare saveButtonTarget: HTMLButtonElement;
  declare saveButtonTextTarget: HTMLElement;

  declare apiUrlValue: string;
  declare saveUrlValue: string;
  declare redirectUrlValue: string;
  declare csrfTokenValue: string;

  declare readonly hasInputTarget: boolean;
  declare readonly hasSaveButtonTarget: boolean;
  declare readonly hasSaveButtonTextTarget: boolean;

  private pond: FilePond.FilePond | null = null;
  private pendingS3Key: string | null = null;
  private hasUnsavedChanges: boolean = false;
  private isSaving: boolean = false;

  connect(): void {
    if (!this.hasInputTarget) {
      console.error('ProfilePhotoUploadController: No input target found');
      return;
    }

    this.initFilePond();
    this.setupBeforeUnloadWarning();
  }

  disconnect(): void {
    if (this.pond) {
      this.pond.destroy();
    }
    this.removeBeforeUnloadWarning();
  }

  private initFilePond(): void {
    this.pond = FilePond.create(this.inputTarget, {
      maxFiles: 1,
      maxFileSize: '5MB',
      acceptedFileTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'],
      allowImagePreview: true,
      imagePreviewHeight: 120,
      stylePanelLayout: 'compact circle',
      styleLoadIndicatorPosition: 'center bottom',
      styleProgressIndicatorPosition: 'right bottom',
      styleButtonRemoveItemPosition: 'left bottom',
      styleButtonProcessItemPosition: 'right bottom',
      credits: false,

      server: {
        process: (fieldName, file, metadata, load, error, progress, abort) => {
          this.uploadToTemp(file as File, load, error, progress);
          return {
            abort: () => {
              abort();
            },
          };
        },
      },

      labelIdle: 'Drag & drop your photo or <span class="filepond--label-action">Browse</span>',
      labelFileProcessing: 'Uploading...',
      labelFileProcessingComplete: 'Ready to save',
      labelFileProcessingError: 'Upload failed',
      labelTapToCancel: 'tap to cancel',
      labelTapToRetry: 'tap to retry',
    });

    this.pond.on('removefile', () => {
      this.pendingS3Key = null;
      this.hasUnsavedChanges = false;
      this.updateSaveButton();
    });
  }

  private async uploadToTemp(
    file: File,
    load: (serverFileId: string) => void,
    error: (errorText: string) => void,
    progress: (computable: boolean, loaded: number, total: number) => void,
  ): Promise<void> {
    try {
      this.hideAllMessages();

      const presignedData = await this.requestPresignedUrl(file);

      await this.uploadToS3(file, presignedData, (progressEvent: ProgressEvent) => {
        progress(progressEvent.lengthComputable, progressEvent.loaded, progressEvent.total);
      });

      this.pendingS3Key = presignedData.s3_key;
      this.hasUnsavedChanges = true;
      this.updateSaveButton();

      load(presignedData.s3_key);
    } catch (err) {
      console.error('Upload to temp failed:', err);
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';
      this.showError(errorMessage);
      error(errorMessage);
    }
  }

  async save(event: Event): Promise<void> {
    event.preventDefault();

    if (!this.pendingS3Key || this.isSaving) {
      return;
    }

    this.isSaving = true;
    this.updateSaveButton();
    this.showStatus('Saving photo...');

    try {
      await this.saveProfilePhoto(this.pendingS3Key);

      this.hasUnsavedChanges = false;
      this.pendingS3Key = null;
      this.showSuccess();

      setTimeout(() => {
        window.location.href = this.redirectUrlValue;
      }, 1500);
    } catch (err) {
      console.error('Save failed:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to save photo';
      this.showError(errorMessage);
      this.isSaving = false;
      this.updateSaveButton();
    }
  }

  private updateSaveButton(): void {
    if (!this.hasSaveButtonTarget) {
      console.log('[ProfilePhoto] No save button target found!');
      return;
    }

    const hasImage = this.pendingS3Key !== null;
    const canSave = hasImage && !this.isSaving;

    console.log('[ProfilePhoto] updateSaveButton:', {
      hasImage,
      canSave,
      isSaving: this.isSaving,
      pendingS3Key: this.pendingS3Key,
    });

    this.saveButtonTarget.disabled = !canSave;

    if (this.isSaving) {
      this.saveButtonTarget.classList.add('opacity-75', 'cursor-wait');
      if (this.hasSaveButtonTextTarget) {
        this.saveButtonTextTarget.textContent = 'Saving...';
      }
    } else {
      this.saveButtonTarget.classList.remove('opacity-75', 'cursor-wait');
      if (this.hasSaveButtonTextTarget) {
        this.saveButtonTextTarget.textContent = 'Save Photo';
      }
    }

    if (canSave) {
      this.saveButtonTarget.classList.remove('bg-gray-300', 'cursor-not-allowed', 'text-white');
      this.saveButtonTarget.classList.add('bg-cta', 'hover:bg-cta-hover', 'text-cta-text');
    } else if (!this.isSaving) {
      this.saveButtonTarget.classList.add('bg-gray-300', 'cursor-not-allowed', 'text-white');
      this.saveButtonTarget.classList.remove('bg-cta', 'hover:bg-cta-hover', 'text-cta-text');
    }
  }

  private setupBeforeUnloadWarning(): void {
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  }

  private removeBeforeUnloadWarning(): void {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  }

  private handleBeforeUnload = (event: BeforeUnloadEvent): string | undefined => {
    if (this.hasUnsavedChanges) {
      const message = 'You have an unsaved photo. Are you sure you want to leave?';
      event.preventDefault();
      event.returnValue = message;
      return message;
    }
    return undefined;
  };

  private async requestPresignedUrl(file: File): Promise<{
    upload_url: string;
    fields: Record<string, string>;
    s3_key: string;
  }> {
    const response = await fetch(this.apiUrlValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': this.csrfTokenValue,
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        filename: file.name,
        content_type: file.type,
        upload_type: 'profile_photo',
        max_file_size_mb: 5,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to get upload URL' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  private async uploadToS3(
    file: File,
    presignedData: { upload_url: string; fields: Record<string, string> },
    onProgress: (event: ProgressEvent) => void,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const formData = new FormData();

      Object.entries(presignedData.fields).forEach(([key, value]) => {
        formData.append(key, value);
      });

      formData.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', onProgress);

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`S3 upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });

      xhr.open('POST', presignedData.upload_url);
      xhr.send(formData);
    });
  }

  private async saveProfilePhoto(s3Key: string): Promise<void> {
    const response = await fetch(this.saveUrlValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': this.csrfTokenValue,
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        s3_key: s3Key,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to save photo' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to save photo');
    }
  }

  private showStatus(message: string): void {
    this.hideAllMessages();
    this.statusTarget.classList.remove('hidden');
    this.statusTextTarget.textContent = message;
  }

  private showError(message: string): void {
    this.hideAllMessages();
    this.errorTarget.classList.remove('hidden');
    this.errorTextTarget.textContent = message;
  }

  private showSuccess(): void {
    this.hideAllMessages();
    this.successTarget.classList.remove('hidden');
  }

  private hideAllMessages(): void {
    this.statusTarget.classList.add('hidden');
    this.errorTarget.classList.add('hidden');
    this.successTarget.classList.add('hidden');
  }
}